import {put, call, all} from 'redux-saga/effects';
import Lookup from "../../entities/lookup";
import * as Actions from "../actions/lookupActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type LookupAction = {
    lookupId: string;
    lookup: Lookup;
    lookups: Lookup[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneLookup(action: LookupAction) {
	const session = tabSession.captureGeneration();
	try {
		const lookup: Lookup = yield call(api.findOne, 'lookup', action.lookupId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_FETCH_ONE_SUCCESS, lookupId: action.lookupId, lookup, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LOOKUP_FETCH_ONE_FAILURE, lookupId: action.lookupId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {LookupAction} action
 */
export function* saveLookup(action: LookupAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ LOOKUP_SAVE saga', action.lookup?.id);

		if (action.lookup === undefined || action.lookup === null) {
			console.error('saveLookup received a null');
			throw new Error('saveLookup received a null');
		}

		if (!(action.lookup instanceof Lookup)) {
			action.lookup = new Lookup(action.lookup);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.lookup.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.lookup);
			const addResult = yield call(idb.add, 'lookup', action.lookup);
			if (addResult.isErr()) {
				console.error(`⮂ saveLookup: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertLookupSave(action.lookup, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.LOOKUP_SAVE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.LOOKUP_SAVE, lookup: action.lookup, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertLookupSave(action.lookup, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.LOOKUP_SAVE_FAILURE, lookup: action.lookup, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {LookupAction} action
 * @param {Lookup[]} action.lookups
 */
export function* saveManyLookups(action: LookupAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ LOOKUP_SAVE_MANY saga', action.lookups.map(lookup => lookup.id).join(','));

        if (!Array.isArray(action.lookups)) {
            console.error('saveManyLookups expected an array, received', action.lookups);
            throw new Error('saveManyLookups expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveLookup), under these queue keys
		const dispatchedAddedOns: number[] = action.lookups.map(lookup => lookup?.addedOn);
		const revertAll = () => action.lookups.map((lookup, i) =>
			put(Actions.revertLookupSave(lookup, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Lookup[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.lookups.length; i++) {
				const lookup = action.lookups[i];
				lookup.persisted = false;
				ensureOperationId(lookup);
				toSave.push(lookup);
			}

			const addResult = yield call(idb.add, 'lookup', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyLookups: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.LOOKUP_SAVE_MANY_FAILURE, lookups: action.lookups, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.LOOKUP_SAVE_MANY, lookups: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.LOOKUP_SAVE_MANY_FAILURE, lookups: action.lookups, status: 500, err});

			console.error(`Error saving ${action.lookups.length} lookups to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {LookupAction} action
 */
export function* removeLookup(action: LookupAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.lookup === undefined || action.lookup === null || !(action.lookup instanceof Lookup)) {
			console.error('removeLookup expected type Lookup, received', action.lookup);
			reportQueued(false);
			throw new Error('removeLookup expected type Lookup');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.lookup.status = 'D';
		action.lookup.addedOn = newHRTimestamp();
		action.lookup.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.lookup);
		const addResult = yield call(idb.add, 'lookup', action.lookup);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeLookup: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.LOOKUP_REMOVE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_REMOVE, lookup: action.lookup});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LOOKUP_REMOVE_FAILURE, lookup: action.lookup, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Lookup.
	*
	* @param {LookupAction} action
	*/
export function* restoreLookup(action: LookupAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreLookup expected getCurrentUserId() to return a value');
        return;
    }

	if (action.lookup === undefined || action.lookup === null) {
        console.error('restoreLookup expected a Lookup, received null');
        throw new Error('restoreLookup expected a Lookup, received null');
    }
    else if (!(action.lookup instanceof Lookup)) action.lookup = new Lookup(action.lookup); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.lookup.status = 'U';
		action.lookup.addedOn = newHRTimestamp();
		action.lookup.addedBy = userId;
		action.lookup.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.lookup);
		const addResult = yield call(idb.add, 'lookup', action.lookup);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreLookup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LOOKUP_RESTORE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_RESTORE, lookup: action.lookup});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.LOOKUP_RESTORE_FAILURE, lookup: action.lookup, status: 500, err});

		console.error('Error restoring from database', err);
	}
}